import { Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { MatStepper, MatStep } from '@angular/material/stepper';
import OrganizationService from 'src/app/shared/services/organization.service';
import { Organization } from '../../organization-models';
import { Location } from '@angular/common';
import { BehaviorSubject, firstValueFrom, of } from 'rxjs';
import { DepartmentSearchEvent } from '../../../department/department.model';
import { filter, switchMap, take, tap } from 'rxjs/operators';
import { MatSort } from '@angular/material/sort';
import { Step1OrganizationComponent } from './step1-organization/step1-organization.component';
import { Step2DepartmentsComponent } from './step2-departments/step2-departments.component';
import { Step2DepartmentFormComponent } from './step2-department-form/step2-department-form.component';
import { Step3AccessGroupsComponent } from './step3-access-groups/step3-access-groups.component';
import { Step4PriceRegulationsComponent } from './step4-price-regulations/step4-price-regulations.component';
import { Step5ModulesComponent } from './step5-modules/step5-modules.component';
import { Step6UsersComponent } from './step6-users/step6-users.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { FEATURE_FLAGS } from 'src/app/shared/services/feature-flags.const';
import { catchError, map } from 'rxjs/operators';
export interface EditDepartmentState {
  id?: number;
  timestamp: number;
}
@Component({
  selector: 'adm-create-organization-wizard',
  templateUrl: './create-organization-wizard.component.html',
  styleUrl: './create-organization-wizard.component.scss',
  imports: [
    MatStepper,
    MatStep,
    Step1OrganizationComponent,
    Step2DepartmentsComponent,
    Step2DepartmentFormComponent,
    Step3AccessGroupsComponent,
    Step4PriceRegulationsComponent,
    Step5ModulesComponent,
    Step6UsersComponent,
  ],
})
export class CreateOrganizationWizardComponent implements OnInit {
  private organizationService = inject(OrganizationService);
  private location = inject(Location);
  private featureFlagService = inject(FeatureFlagService);

  @ViewChild('stepper') stepper: MatStepper;

  public organizationId = signal<number | null>(null);
  public step1Label = signal('Opret organisation');
  public step2Label = signal('Opret afdelinger');
  public step3Label = 'Opret adgangsgrupper';
  public step4Label = 'Opret prisregulering';
  public step5Label = 'Tilknyt moduler';
  public step6Label = 'Tilknyt portalbrugere';

  public organization = signal<Organization | null>(null);
  public newDepartmentId = signal<number>(0);
  public editDepartmentId = signal<EditDepartmentState>({ id: undefined, timestamp: Date.now() });

  private reloadDepartmentsSubject = new BehaviorSubject<void>(undefined);
  public departments = toSignal(
    this.reloadDepartmentsSubject.pipe(
      filter(() => !!this.organization()?.id),
      switchMap(() => {
        const organization = this.organization();
        if (!organization) {
          return of([]);
        }
        const departmentSearchEvent = new DepartmentSearchEvent();
        departmentSearchEvent.organizationId = organization.id;
        return this.organizationService.getDepartmentsForOrganization(organization.id, new MatSort());
      })
    ),
    { initialValue: [] }
  );

  public priceRegulationsFeatureEnabled = toSignal(
    this.featureFlagService.getFeatureFlag(FEATURE_FLAGS.PRICE_REGULATIONS_READ).pipe(
      map(flag => flag.result),
      catchError(() => of(false))
    ),
    { initialValue: false }
  );

  ngOnInit() {
    const organizationId = this.getOrganizationIdFromHash();
    if (organizationId) {
      const parsedOrganizationId = parseInt(organizationId, 10);
      this.organizationId.set(parsedOrganizationId);
      this.refreshStep1(parsedOrganizationId);
    }
  }

  updateDepartments() {
    this.reloadDepartmentsSubject.next();
  }

  refreshStep1(organizationId: number) {
    this.organizationId.set(organizationId);
    this.organizationService
      .getOrganization(organizationId)
      .pipe(
        take(1),
        tap(organization => {
          this.organization.set(organization);
          this.step1Label.set('Organisation oprettet: ' + organization.name);
          this.step2Label.set('Opret afdelinger under ' + organization.name);
          const currentPath = this.location.path();
          this.location.replaceState(`${currentPath}#${organization.id}`);
          this.nextStep();
        })
      )
      .subscribe();
  }
  refreshStep2(event) {
    const departmentId: number = event.departmentId;
    this.newDepartmentId.set(departmentId);
  }
  loadDepartmentForm(event: any) {
    const dep: EditDepartmentState = event;
    this.editDepartmentId.set(dep);
  }

  nextStep() {
    this.stepper.next();
  }

  onStepChange(event: StepperSelectionEvent): void {
    const stepIndex = event.selectedIndex;

    if (stepIndex === 1 && this.organization()) {
      this.updateDepartments();
    }
  }

  private getOrganizationIdFromHash(): string {
    return window.location.hash.substring(1); // Remove the '#' character
  }
}
